import { css } from '@mildang/styled-system/css';
import type { SystemStyleObject } from '@mildang/styled-system/types';

/**
 * Button 의 variant 조합 스타일. 순서가 곧 우선순위다(뒤가 앞을 덮는다).
 *
 * recipe 의 compoundVariants 로 두지 않는다. v1(Panda 1)은 compound 를 런타임에 atomic 클래스(utilities 레이어)로
 * 붙여 선언 순서대로 합치고 Emotion(mui_emotion 레이어)도 이겼는데, Panda 2 config recipe 는 compound 를
 * recipes 레이어에 클래스 이름 순서로 내보내 둘 다 달라진다. 그래서 Button 이 `css()` 로 같은 방식으로 붙인다.
 */
type ButtonCompound = {
  variant?: string;
  color?: string;
  size?: string;
  selected?: boolean;
  strong?: boolean;
  root: SystemStyleObject;
};

const buttonCompounds: ButtonCompound[] = [
  { variant: 'text', size: 'xLarge', root: css.raw({ padding: '[11px 12px]' }) },
  { variant: 'text', size: 'large', root: css.raw({ padding: '[8px 10px]' }) },
  { variant: 'text', size: 'medium', root: css.raw({ padding: '[6px 8px]' }) },
  { variant: 'text', size: 'small', root: css.raw({ padding: '[6px 6px]' }) },
  {
    variant: 'text',
    color: 'primary',
    root: css.raw({
      color: 'common.10',
      _hover: { background: 'common.a1' },
      _active: { _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'text',
    color: 'secondary',
    root: css.raw({
      color: 'success.10',
      _hover: { background: 'success.a1' },
      _active: { _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'text',
    color: 'error',
    root: css.raw({
      color: 'error.10',
      _hover: { background: 'error.a1' },
      _active: { _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'text',
    selected: true,
    color: 'primary',
    root: css.raw({
      background: 'success.a2',
      color: 'success.10',
      _hover: { background: 'success.a3' },
      _active: { _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'text',
    selected: true,
    color: 'secondary',
    root: css.raw({
      background: 'success.a2',
      color: 'success.10',
      _hover: { background: 'success.a3' },
      _active: { _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'text',
    selected: true,
    color: 'error',
    root: css.raw({
      background: 'error.a2',
      color: 'error.10',
      _hover: { background: 'error.a3' },
      _active: { _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'contained',
    color: 'impact',
    root: css.raw({
      boxShadow: `0px 0px 0px 4px rgba(197, 80, 237, 0.3), 0px 0px 0px 4px rgba(31, 114, 239, 0.3)`,
      background: 'common.gradient.purpleBlue',
      color: 'common.1',
      transition: `transform 0.2s, box-shadow 0.2s`,
      animation: `animatedGradient 2.8s ease infinite alternate, glowPulse 1.5s ease infinite`,
      _hover: {
        background:
          'linear-gradient(0deg, token(colors.common.a3) 0%, token(colors.common.a3) 100%), linear-gradient(135deg, #C550ED 0%, #1F72EF 100%)',
        boxShadow: 'none',
        transition: 'none',
        animation: 'none',
      },
      _active: {
        boxShadow: 'none',
        background: 'common.gradient.purpleBlue',
        transition: 'none',
        animation: 'none',
        _after: { opacity: 1 },
      },
      _disabled: {
        boxShadow: 'none',
        transition: 'none',
        animation: 'none',
      },
    }),
  },
  {
    variant: 'contained',
    color: 'primary',
    root: css.raw({
      color: 'common.1',
      background: 'common.7',
      _hover: { background: 'common.8' },
      _active: { background: 'common.9' },
    }),
  },
  {
    variant: 'contained',
    color: 'secondary',
    root: css.raw({
      background: 'success.7',
      color: 'white.a1300',
      _hover: { background: 'success.8' },
      _active: { _before: { opacity: 1 } },
    }),
  },
  {
    variant: 'contained',
    color: 'error',
    root: css.raw({
      background: 'error.7',
      color: 'white.a1300',
      _hover: { background: 'error.8' },
      _disabled: { background: 'error.7' },
      _active: { _before: { opacity: 1 } },
    }),
  },
  {
    variant: 'contained',
    selected: true,
    color: 'primary',
    root: css.raw({
      background: 'success.7',
      color: 'common.1',
      _hover: { background: 'success.8' },
      _active: { background: 'success.8', _before: { opacity: 1 } },
    }),
  },
  {
    variant: 'contained',
    selected: true,
    color: 'secondary',
    root: css.raw({
      background: 'success.9',
      color: 'white.a1300',
      _hover: { background: 'success.8' },
      _active: { _before: { opacity: 1 } },
    }),
  },
  {
    variant: 'contained',
    selected: true,
    color: 'error',
    root: css.raw({
      background: 'error.9',
      color: 'white.a1300',
      _hover: { background: 'error.8' },
      _active: { _before: { opacity: 1 } },
    }),
  },
  {
    variant: 'outlined',
    color: 'primary',
    root: css.raw({
      border: '1px solid token(colors.common.a2)',
      color: 'common.10',
      _hover: { border: '1px solid token(colors.common.6)', background: 'common.a1' },
      _disabled: { border: '1px solid token(colors.common.a2)' },
      _active: { _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'outlined',
    color: 'secondary',
    root: css.raw({
      border: '1px solid token(colors.success.5)',
      color: 'success.10',
      _hover: { border: '1px solid token(colors.success.6)', background: 'success.a1' },
      _disabled: { border: '1px solid token(colors.success.5)' },
      _active: { _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'outlined',
    color: 'error',
    root: css.raw({
      border: '1px solid token(colors.error.5)',
      color: 'error.10',
      _hover: { border: '1px solid token(colors.error.6)', background: 'error.a1' },
      _disabled: { border: '1px solid token(colors.error.5)' },
      _active: { _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'outlined',
    strong: true,
    root: css.raw({
      border: '1px solid token(colors.common.7)',
      color: 'common.12',
      _hover: { border: '1px solid token(colors.common.8)', background: 'common.a1' },
      _disabled: { border: '1px solid token(colors.common.7)', background: 'common.a1' },
    }),
  },
  {
    variant: 'outlined',
    selected: true,
    color: 'primary',
    root: css.raw({
      background: 'success.a3',
      color: 'success.10',
      borderColor: 'success.7',
      _hover: { background: 'success.a1', borderColor: 'success.7' },
      _active: { background: 'success.a3', _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'outlined',
    selected: true,
    color: 'secondary',
    root: css.raw({
      background: 'success.a3',
      color: 'success.10',
      borderColor: 'success.7',
      _hover: { background: 'success.a1', borderColor: 'success.7' },
      _active: { background: 'success.a3', _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'outlined',
    selected: true,
    color: 'error',
    root: css.raw({
      background: 'error.a2',
      color: 'error.10',
      borderColor: 'error.7',
      _hover: { background: 'error.a1', borderColor: 'error.7' },
      _active: { background: 'error.a3', _after: { opacity: 1 } },
    }),
  },
  {
    variant: 'outlined',
    strong: true,
    selected: true,
    root: css.raw({
      background: 'success.a2',
      color: 'success.10',
      border: '1px solid token(colors.success.10)',
      _hover: { background: 'success.a3' },
      _active: { _after: { opacity: 1 } },
    }),
  },
];

type ButtonCompoundProps = Omit<ButtonCompound, 'root'>;
const conditionKeys: Array<keyof ButtonCompoundProps> = ['variant', 'color', 'size', 'selected', 'strong'];

export const getButtonCompoundClassName = (props: ButtonCompoundProps) =>
  css(
    ...buttonCompounds
      .filter((compound) =>
        conditionKeys.every((key) => compound[key] === undefined || compound[key] === props[key]),
      )
      .map(({ root }) => root),
  );
